// XmlCdr.jsx
import { useState, useMemo } from "react";
import "../../../../src/style.css";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDispatch, } from "react-redux";
import useReportStyles from "./styles/reportStyles"; // Import styles

import {
  getRedirectBuyerDialog,
  createUserBlockReport,
} from "../../../redux/actions/redirectPortal/redirectPortal_reportAction";

import { useReportData } from "./hooks/useReportData";
import { useReportFilters } from "./hooks/useReportFilters";
import { useAudioPlayer } from "./hooks/useAudioPlayer";
import { ReportFilters } from "./components/ReportFilters";
import { ReportTable } from "./components/ReportTable";
import { CallDetailsModal } from "./components/CallDetailsModal";
import PageTitle from "../../../components/common/PageTitle";
import { exportToCSV } from "./utils/csvExporter";
import ClearIcon from "@mui/icons-material/Clear";
import Datetime from "react-datetime";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";

function XmlCdr({ userThem }) {
  const classes = useReportStyles(); // Initialize styles
  const dispatch = useDispatch();

  // Get user permissions
  const current_user = localStorage.getItem("current_user");
  const blocked_features = JSON.parse(
    localStorage.getItem(`user_${current_user}`) || '{"blocked_features":[]}',
  );
  const isCallBlockAllowed =
    blocked_features.blocked_features?.includes("call_block") || false;

  // Custom hooks
  const {
    searchDestination,
    setSearchDestination,
    filters,
    fromDate,
    toDate,
    handleFromDateChange,
    handleToDateChange,
    handleFilterChange,
    resetFilters,
  } = useReportFilters();

  const { rows, loading, search, buyerDialogs } = useReportData(
    fromDate,
    toDate,
    filters,
  );
  const audioControls = useAudioPlayer();

  // Local state
  const [openModal, setOpenModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [infoOpen, setInfoOpen] = useState(false);

  // Define columns for export (memoize to prevent unnecessary re-renders)
  const exportColumns = useMemo(
    () => [
      { field: "caller_id_number", headerName: "Caller ID" },
      { field: "did_tfn", headerName: "DID Number" },
      { field: "forwarded_number", headerName: "Forwarded" },
      { field: "hangup_reason", headerName: "Status" },
      { field: "call_result", headerName: "Call Status" },
      { field: "buyer_name", headerName: "Buyer Name" },
      { field: "campaign_name", headerName: "Campaign Name" },
      { field: "duration", headerName: "Duration" },
      { field: "start_at", headerName: "Call Start" },
      { field: "end_at", headerName: "Call End" },
      { field: "hangupby", headerName: "Hangup By" },
      { field: "uniqueid", headerName: "Unique ID" },
      { field: "answer_at", headerName: "Call Answer" },
    ],
    [],
  );

  const handleOpenModal = (row) => {
    const data = JSON.stringify({ uniqueid: row.uniqueid });
    setSelectedRow(row);
    dispatch(getRedirectBuyerDialog(data));
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedRow(null);
  };

  const handleSelectionChange = (selection) => {
    setSelectedRows(selection);
  };

  const handleSearch = () => {
    search();
  };

  const handleReset = () => {
    resetFilters();
    setSearchDestination("");
  };

  const handleExport = () => {
    exportToCSV(
      rows,
      exportColumns,
      `report_${new Date().toISOString().slice(0, 10)}.csv`,
    );
  };

  const handleBlockCallerIds = () => {
    const selectedCallerDataMap = new Map();

    selectedRows.forEach((id) => {
      const selectedRow = rows.find((row) => row.id === id);
      if (selectedRow) {
        const userId = selectedRow.user_uuid;
        if (!selectedCallerDataMap.has(userId)) {
          selectedCallerDataMap.set(userId, {
            type: "CallerID",
            details: [],
            description: "Report",
            user_id: JSON.stringify(userId),
            is_active: true,
          });
        }
        const data = selectedCallerDataMap.get(userId);
        if (!data.details.includes(selectedRow.caller_id_number)) {
          data.details.push(selectedRow.caller_id_number);
        }
      }
    });

    const selectedCallerData = Array.from(selectedCallerDataMap.values());
    if (selectedCallerData.length > 0) {
      dispatch(createUserBlockReport(JSON.stringify(selectedCallerData)));
    }
  };

  // Filter rows based on search + duration
  const durationThreshold =
    filters.durationValue !== "" && !Number.isNaN(Number(filters.durationValue))
      ? Number(filters.durationValue)
      : null;
  const matchesDuration = (row) => {
    if (durationThreshold === null) return true;
    const d = Number(row.duration);
    if (Number.isNaN(d)) return false;
    switch (filters.durationOp) {
      case "=":  return d === durationThreshold;
      case "!=": return d !== durationThreshold;
      case "<":  return d < durationThreshold;
      case "<=": return d <= durationThreshold;
      case ">":  return d > durationThreshold;
      case ">=":
      default:   return d >= durationThreshold;
    }
  };

  const searchedRows = rows.filter(
    (row) =>
      (row.call_result
        ?.toLowerCase()
        .includes(searchDestination.toLowerCase()) ||
        row.did_tfn?.toLowerCase().includes(searchDestination.toLowerCase()) ||
        row.caller_id_number
          ?.toLowerCase()
          .includes(searchDestination.toLowerCase()) ||
        row.forwarded_number
          ?.toLowerCase()
          .includes(searchDestination.toLowerCase())) &&
      matchesDuration(row),
  );

  // When "Unique" is checked, dedupe by uniqueid — first occurrence wins.
  const filteredRows = filters.uniqueOnly
    ? (() => {
        const seen = new Set();
        const out = [];
        for (const row of searchedRows) {
          const key = row.uniqueid;
          if (key == null || key === "") {
            out.push(row);
            continue;
          }
          if (!seen.has(key)) {
            seen.add(key);
            out.push(row);
          }
        }
        return out;
      })()
    : searchedRows;

  return (
    <div className={`App ${userThem} ${classes.root}`}>
      <div className="contant_box">
        <div className="main">
          <section className="sidebar-sec">
            <div className="container-fluid">
              <div className="row">
                <div className="col-lg-12">
                  <div className="tab-content">
                    <div className="tab-pane fade show active">
                      <div className="tab_cntnt_box">
                        <Grid
                          container
                          className="cntnt_title"
                          sx={{
                            // 3-column CSS grid: title (left), dates (center),
                            // spacer (right). The 1fr side columns absorb
                            // slack equally so the middle column — the dates
                            // — is perfectly page-centered regardless of the
                            // title's width. Below `sm` it stacks vertically.
                            display: { xs: "flex", sm: "grid" },
                            gridTemplateColumns: { sm: "1fr auto 1fr" },
                            alignItems: "center",
                            flexWrap: "wrap",
                            columnGap: 2,
                            rowGap: 1,
                          }}
                        >
                          {/* Heading — left column on sm+ */}
                          <Grid
                            item
                            xs={12}
                            sm="auto"
                            sx={{ justifySelf: { sm: "start" } }}
                          >
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <PageTitle title="Report" />
                              <Tooltip title="How to use the Report page" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => setInfoOpen(true)}
                                  sx={{ color: "#f5751D", mt: "-8px" }}
                                >
                                  <InfoOutlinedIcon />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Grid>

                          {/* From / To date pickers — center column,
                              page-centered on sm+. */}
                          <Grid
                            item
                            xs={12}
                            sm="auto"
                            sx={{ justifySelf: { sm: "center" } }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  minWidth: 170,
                                  maxWidth: 210,
                                }}
                              >
                                <label
                                  style={{
                                    fontSize: "13px",
                                    paddingRight: "6px",
                                    fontWeight: 500,
                                    minWidth: "35px",
                                  }}
                                >
                                  From:
                                </label>
                                <Datetime
                                  inputProps={{
                                    style: {
                                      height: "32px",
                                      padding: "4px 8px",
                                      fontSize: "13px",
                                      width: "100%",
                                    },
                                  }}
                                  style={{ width: "100%", margin: 0 }}
                                  className="datefield_select new_input"
                                  value={fromDate}
                                  onChange={handleFromDateChange}
                                  closeOnSelect
                                  dateFormat="DD/MM/YYYY"
                                  timeFormat="HH:mm"
                                />
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  minWidth: 170,
                                  maxWidth: 210,
                                }}
                              >
                                <label
                                  style={{
                                    fontSize: "13px",
                                    paddingRight: "6px",
                                    fontWeight: 500,
                                    minWidth: "35px",
                                  }}
                                >
                                  To:
                                </label>
                                <Datetime
                                  inputProps={{
                                    style: {
                                      height: "32px",
                                      padding: "4px 8px",
                                      fontSize: "13px",
                                      width: "100%",
                                    },
                                  }}
                                  style={{ width: "100%", margin: 0 }}
                                  className="datefield_select new_input"
                                  value={toDate}
                                  onChange={handleToDateChange}
                                  closeOnSelect
                                  dateFormat="DD/MM/YYYY"
                                  timeFormat="HH:mm"
                                />
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>

                        <ReportFilters
                          filters={filters}
                          onFilterChange={handleFilterChange}
                          fromDate={fromDate}
                          toDate={toDate}
                          onFromDateChange={handleFromDateChange}
                          onToDateChange={handleToDateChange}
                          onSearch={handleSearch}
                          onReset={handleReset}
                          onExport={handleExport}
                          onBlock={handleBlockCallerIds}
                          selectedCount={selectedRows.length}
                          isCallBlockAllowed={isCallBlockAllowed}
                          classes={classes} // Pass classes if needed in child components
                        />

                        <ReportTable
                          rows={filteredRows}
                          loading={loading}
                          onRowClick={handleOpenModal}
                          onSelectionChange={handleSelectionChange}
                          selectedRows={selectedRows}
                          isCallBlockAllowed={isCallBlockAllowed}
                          audioControls={audioControls}
                          searchDestination={searchDestination}
                          onSearchDestinationChange={setSearchDestination}
                        />

                        <CallDetailsModal
                          open={openModal}
                          onClose={handleCloseModal}
                          selectedRow={selectedRow}
                          buyerDialogs={buyerDialogs}
                          audioControls={audioControls}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Report help dialog — opened by the (i) icon next to the page title.
          Pattern mirrors SipTracer's "How It Works" dialog and AdminPortalAuditLogs'
          coverage modal. Explicit dark text + !important defeats the global
          `color: #fff !important` rules in style.css that would otherwise
          render this content invisible on the white MUI Paper surface. */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <InfoOutlinedIcon sx={{ color: "#f5751D" }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#212121 !important" }}>
              How to use the Report page
            </Typography>
          </Stack>
          <IconButton size="small" onClick={() => setInfoOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ "& *": { color: "#212121 !important" } }}>
          <Typography variant="body2" sx={{ mb: 2, fontSize: "0.92rem" }}>
            The Report page lists every call routed through your DIDs in a
            given date range. Use the filters to narrow the list, the table
            search to scan results, and <b>Download</b> to export a CSV of
            the rows currently showing.
          </Typography>

          {/* QUICK START */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, mt: 1, color: "#0a140e !important" }}>
            Quick start
          </Typography>
          <Box component="ol" sx={{ pl: 2.5, "& li": { mb: 0.6 }, fontSize: "0.88rem" }}>
            <li>Pick a <b>From</b> and <b>To</b> date/time (defaults to today, 00:00 → 23:59).</li>
            <li>Optional: type into Caller ID / DID Number / Forward Number, or set a duration filter.</li>
            <li>Click <b>Apply Filters</b> to run the query.</li>
            <li>Use the <b>Search</b> box above the table to scan within the results.</li>
            <li>Click <b>Download</b> to export the visible rows to CSV.</li>
          </Box>

          {/* DATE RANGE */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, mt: 2.5, color: "#0a140e !important" }}>
            From / To dates (always applied)
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, "& li": { mb: 0.5 }, fontSize: "0.88rem" }}>
            <li>Date format: <code>DD/MM/YYYY</code>. Time format: <code>HH:mm</code> (24-hour).</li>
            <li>Default range is today, midnight to end-of-day.</li>
            <li><b>Smart time:</b> if you only change the date, the time stays at what you had. If you set From/To dates without picking a time, the time defaults to "now".</li>
            <li><b>Examples:</b>
              <Box component="ul" sx={{ pl: 2.5, mt: 0.3, "& li": { mb: 0.2 } }}>
                <li><i>Last 24 hours from now:</i> set From = yesterday now, To = today now.</li>
                <li><i>Yesterday only:</i> From = yesterday 00:00, To = yesterday 23:59.</li>
                <li><i>Last 7 days:</i> From = today − 6 days at 00:00, To = today 23:59.</li>
                <li><i>Specific peak hour:</i> From = today 14:00, To = today 14:59.</li>
              </Box>
            </li>
          </Box>

          {/* TEXT FILTERS */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, mt: 2.5, color: "#0a140e !important" }}>
            Caller ID / DID Number / Forward Number
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, "& li": { mb: 0.5 }, fontSize: "0.88rem" }}>
            <li>All three are <b>partial-match</b> (substring), case-insensitive. You don't need an exact full number.</li>
            <li><b>Caller ID</b> — matches against the caller's number who dialed in.
              <Box component="ul" sx={{ pl: 2.5, mt: 0.3, "& li": { mb: 0.2 } }}>
                <li><i>Find a single repeat caller:</i> <code>15551234567</code></li>
                <li><i>Find anyone from a US area code:</i> <code>1313</code> (matches all 313-area calls)</li>
                <li><i>Find international:</i> <code>44</code> (matches +44 UK numbers)</li>
              </Box>
            </li>
            <li><b>DID Number</b> — matches the DID/TFN that received the call.
              <Box component="ul" sx={{ pl: 2.5, mt: 0.3, "& li": { mb: 0.2 } }}>
                <li><i>Calls to one DID:</i> <code>18005551212</code></li>
                <li><i>All toll-free traffic:</i> <code>1800</code></li>
              </Box>
            </li>
            <li><b>Forward Number</b> — matches the destination the call was forwarded to.
              <Box component="ul" sx={{ pl: 2.5, mt: 0.3, "& li": { mb: 0.2 } }}>
                <li><i>Calls routed to a specific buyer line:</i> <code>15558889999</code></li>
                <li><i>Everything that went to one prefix:</i> <code>1212</code></li>
              </Box>
            </li>
            <li><b>Tip:</b> filters combine with AND — all criteria must match.</li>
          </Box>

          {/* DURATION */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, mt: 2.5, color: "#0a140e !important" }}>
            Duration filter (Op + Sec)
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, "& li": { mb: 0.5 }, fontSize: "0.88rem" }}>
            <li>Pick an <b>Op</b> (operator) and a <b>Sec</b> (seconds) value. Default operator is <code>≥</code> (greater-than-or-equal).</li>
            <li>Operators: <code>=</code>, <code>≠</code>, <code>&lt;</code>, <code>≤</code>, <code>&gt;</code>, <code>≥</code>.</li>
            <li><b>Examples:</b>
              <Box component="ul" sx={{ pl: 2.5, mt: 0.3, "& li": { mb: 0.2 } }}>
                <li><i>Find serious conversations:</i> Op <code>≥</code>, Sec <code>120</code> (calls 2 min+).</li>
                <li><i>Find quick hangups:</i> Op <code>&lt;</code>, Sec <code>10</code> (calls under 10 sec).</li>
                <li><i>Exact-length test calls:</i> Op <code>=</code>, Sec <code>30</code>.</li>
                <li><i>Exclude exactly-zero calls:</i> Op <code>≠</code>, Sec <code>0</code>.</li>
              </Box>
            </li>
            <li>Leave <b>Sec</b> empty to skip the duration filter entirely.</li>
          </Box>

          {/* UNIQUE */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, mt: 2.5, color: "#0a140e !important" }}>
            Unique checkbox
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, "& li": { mb: 0.5 }, fontSize: "0.88rem" }}>
            <li>Off (default): every row in the CDR is shown — a single call may appear once.</li>
            <li>On: hides duplicate rows by <b>Unique ID</b>, keeping only the first occurrence. Use this when a call has multiple log rows (e.g. retried hops) and you want a cleaner per-call view.</li>
            <li><b>Example:</b> a call that tried 3 buyers before connecting may show 3 rows; with <b>Unique</b> on, you see just 1.</li>
          </Box>

          {/* APPLY / RESET */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, mt: 2.5, color: "#0a140e !important" }}>
            Apply Filters / Reset
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, "& li": { mb: 0.5 }, fontSize: "0.88rem" }}>
            <li>Filters do <b>not</b> apply automatically as you type. Click <b>Apply Filters</b> to run the query.</li>
            <li><b>Reset</b> clears every filter and snaps the date range back to today (midnight → end-of-day).</li>
          </Box>

          {/* TABLE SEARCH */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, mt: 2.5, color: "#0a140e !important" }}>
            Table search (different from filters!)
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, "& li": { mb: 0.5 }, fontSize: "0.88rem" }}>
            <li>The <b>Search</b> box above the table runs <i>after</i> filters — it scans the rows currently loaded, across <b>Caller ID, DID, Forward Number, and Status</b> at once.</li>
            <li>Use it for a quick "find" within results without re-running the API query.</li>
            <li><b>Example:</b> apply Caller ID = <code>1313</code> + Apply Filters → then type <code>busy</code> in Search to see only busy calls in that subset.</li>
          </Box>

          {/* EXPORT */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, mt: 2.5, color: "#0a140e !important" }}>
            Download (CSV export)
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, "& li": { mb: 0.5 }, fontSize: "0.88rem" }}>
            <li>Click <b>Download</b> to save the rows currently showing as <code>report_YYYY-MM-DD.csv</code>.</li>
            <li>The CSV honors your filters and the table search — only what's visible is exported.</li>
            <li><b>Columns exported:</b> Caller ID, DID Number, Forwarded, Status, Call Status, Buyer Name, Campaign Name, Duration, Call Start, Call End, Hangup By, Unique ID, Call Answer.</li>
            <li><b>Tip:</b> for a daily report, set From/To to a full day, click Apply, then Download — opens cleanly in Excel / Google Sheets / LibreOffice.</li>
            <li><b>Tip:</b> for a slice (e.g., long calls only), apply the Duration filter first, then Download — the CSV reflects exactly the slice.</li>
          </Box>

          {/* COMMON WORKFLOWS */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, mt: 2.5, color: "#0a140e !important" }}>
            Common workflows
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, "& li": { mb: 0.5 }, fontSize: "0.88rem" }}>
            <li><b>"Why did this caller hang up?"</b> — Caller ID = their number, Apply, click any row to open the Call Details modal.</li>
            <li><b>"How many calls landed on DID X today?"</b> — DID Number = X, leave dates default, Apply, glance at the row count.</li>
            <li><b>"Which calls actually converted to a real conversation?"</b> — Op = <code>≥</code>, Sec = <code>30</code>, Apply.</li>
            <li><b>"Audit one buyer's traffic for the week"</b> — Forward Number = buyer's line, From = 7 days ago, To = today 23:59, Apply, then Download.</li>
            <li><b>"De-noise multi-hop calls"</b> — turn on <b>Unique</b> to collapse retry rows.</li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} variant="contained" sx={{ textTransform: "none" }}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}


export default XmlCdr;






